<script setup lang="ts">
import type { FieldDefinition } from '@/types/calculator'

defineProps<{
  id: string
  modelValue: string
  label?: string
  options: NonNullable<FieldDefinition['options']>
  required?: boolean
  error?: string
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
}>()

function onChange(event: Event) {
  const target = event.target as HTMLSelectElement
  emit('update:modelValue', target.value)
}
</script>

<template>
  <div class="field">
    <label v-if="label" :for="id" class="field__label">{{ label }}</label>
    <select
      :id="id"
      :value="modelValue"
      :required="required"
      class="field__select"
      :class="{ 'field__select--error': error }"
      @change="onChange"
    >
      <option v-for="opt in options" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
    </select>
    <p v-if="error" class="field__error" role="alert">{{ error }}</p>
  </div>
</template>

<style scoped>
.field {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.field__label {
  font-weight: var(--font-medium);
  font-size: var(--text-sm);
}

.field__select {
  width: 100%;
  padding: var(--space-2) var(--space-3);
  border: 1px solid var(--color-border-strong);
  border-radius: var(--radius-md);
  background-color: var(--color-surface);
  color: var(--color-text);
  font-size: var(--text-base);
  cursor: pointer;
}

.field__select:focus-visible {
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px rgb(10 92 86 / 0.15);
}

.field__select--error {
  border-color: var(--color-error);
}

.field__error {
  font-size: var(--text-sm);
  color: var(--color-error);
}
</style>
